
import React from 'react';
import Image from '@/components/AppImage/AppImage';
import logoIcon from '@/assets/logo.svg';
import CloseButton from '@/components/Buttons/CloseButton/CloseButton';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import {
  TRIP_IMPORT_ENTRY_SUBTITLE,
  TRIP_IMPORT_ENTRY_TITLE,
} from '@/constants/tripImportEntryCopy';

export interface ImportInspoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateListClick: () => void;
  onCreateTripClick: () => void;
  onSocialImportClick: () => void;
  onTripImportClick: () => void;
}

const DESKTOP_CARDS = [
  {
    key: 'list',
    image: '/images/start-building-panel-list.png',
    title: 'Create List',
    subtitle: 'Save places you love',
    action: 'onCreateListClick' as const,
  },
  {
    key: 'trip',
    image: '/images/start-building-panel-itinerary.png',
    title: 'Create Trip',
    subtitle: 'Plan an itinerary',
    action: 'onCreateTripClick' as const,
  },
  {
    key: 'social',
    image: '/images/start-building-panel-social.png',
    title: 'Import from social',
    subtitle: 'Pull places from posts with AI',
    action: 'onSocialImportClick' as const,
  },
  {
    key: 'trip-import',
    image: '/images/start-building-panel-itinerary.png',
    title: TRIP_IMPORT_ENTRY_TITLE,
    subtitle: TRIP_IMPORT_ENTRY_SUBTITLE,
    action: 'onTripImportClick' as const,
  },
] as const;

const MOBILE_ROWS = [
  {
    key: 'list',
    iconSrc: '/images/start-building-mobile-icon-list.png',
    title: 'Create List',
    subtitle: 'Save places you love',
    action: 'onCreateListClick' as const,
  },
  {
    key: 'trip',
    iconSrc: '/images/start-building-mobile-icon-trip.png',
    title: 'Create Trip',
    subtitle: 'Plan an itinerary',
    action: 'onCreateTripClick' as const,
  },
  {
    key: 'social',
    iconSrc: '/images/start-building-mobile-icon-social.png',
    title: 'Import from social',
    subtitle: 'Pull places from posts with AI',
    action: 'onSocialImportClick' as const,
  },
  {
    key: 'trip-import',
    iconSrc: '/images/start-building-mobile-icon-trip.png',
    title: TRIP_IMPORT_ENTRY_TITLE,
    subtitle: TRIP_IMPORT_ENTRY_SUBTITLE,
    action: 'onTripImportClick' as const,
  },
] as const;

const GRADIENT_BORDER_STYLE: React.CSSProperties = {
  background: 'linear-gradient(-45deg, #FF7629 2%, #E3F0AD 22%, #D6CCFF 55%, #FF7629 89%)',
};

const ImportInspoModal: React.FC<ImportInspoModalProps> = ({
  isOpen,
  onClose,
  onCreateListClick,
  onCreateTripClick,
  onSocialImportClick,
  onTripImportClick,
}) => {
  const isMobileViewport = useIsMobileViewport();

  if (!isOpen) return null;

  const handlers = {
    onCreateListClick,
    onCreateTripClick,
    onSocialImportClick,
    onTripImportClick,
  };

  const handleCardClick = (action: (typeof DESKTOP_CARDS)[number]['action']) => {
    onClose();
    handlers[action]();
  };

  const handleMobileRowClick = (action: (typeof MOBILE_ROWS)[number]['action']) => {
    onClose();
    handlers[action]();
  };

  // Render only the variant that matches the current viewport. Hand-rolled
  // panels intentionally do not get the `mobile-sheet` shell class — that
  // class declares `display: flex` and (due to source order) overrides
  // Tailwind's `hidden` utility, which previously made BOTH variants visible
  // on desktop.
  return (
    <ResponsiveOverlay
      onClose={onClose}
      presentation="sheet"
      position={isMobileViewport ? 'center' : 'right-side'}
      unwrapped
      showSheetHandle={false}
      className="import-inspo-modal-overlay !z-[10050]"
      aria-label="Start building menu"
    >
      {isMobileViewport ? (
        <div
          className="relative flex w-full max-h-[min(85dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-32px))] shrink-0 flex-col overflow-hidden rounded-t-[20px] border-2 border-b-0 border-[#FF7629] shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] bg-white"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-inspo-modal-title-mobile"
        >
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <Image
              src="/images/start-building-mobile-bg.png"
              alt=""
              fill
              className="object-cover"
              sizes="100vw"
              priority
            />
          </div>

          <CloseButton
            onClick={onClose}
            ariaLabel="Close start building menu"
            size={24}
            className="absolute right-[22px] top-[22px] z-20 text-[#252528]"
          />

          <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden gt-keyboard-scroll px-4 pb-[max(24px,env(safe-area-inset-bottom,0px))] pt-16">
            <div className="flex flex-col items-center gap-8">
              <Image src={logoIcon} alt="Globe Thrivers" width={145} height={32} className="h-8 w-auto" priority />

              <h2
                id="import-inspo-modal-title-mobile"
                className="font-lora text-center text-[22px] font-semibold leading-[28px] tracking-[0.44px] text-[#252528]"
              >
                <span className="block">Start Building </span>
                <span className="block">Your Dream Trip</span>
              </h2>

              <div className="flex w-full flex-col gap-2.5">
                {MOBILE_ROWS.map((row) => (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => handleMobileRowClick(row.action)}
                    className="flex w-full items-center gap-4 rounded-2xl bg-white/50 p-6 text-left transition-colors hover:bg-white/70"
                  >
                    <Image src={row.iconSrc} alt="" width={24} height={24} className="shrink-0" />
                    <div className="flex min-w-0 flex-1 flex-col gap-1 text-[#252528]">
                      <span className="font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px]">
                        {row.title}
                      </span>
                      <span className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px]">
                        {row.subtitle}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="relative w-full max-w-[920px] shrink-0 rounded-[20px] p-[2px] shadow-modal"
          style={GRADIENT_BORDER_STYLE}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-inspo-modal-title-desktop"
        >
          <div className="relative overflow-hidden rounded-[18px] bg-white">
            <CloseButton
              onClick={onClose}
              ariaLabel="Close start building menu"
              size={24}
              className="absolute right-6 top-6 z-10 text-[#252528]"
            />

            <Image
              src="/images/start-building-bg.png"
              alt=""
              fill
              className="pointer-events-none object-cover"
              priority
            />

            <div className="relative flex flex-col items-center gap-6 p-6 pb-8 min-[768px]:gap-8 min-[768px]:p-8">
              <Image src={logoIcon} alt="Globe Thrivers" width={218} height={48} />

              <h2
                id="import-inspo-modal-title-desktop"
                className="font-lora text-center text-[32px] font-semibold leading-[42px] tracking-[0.016em] text-[#252528]"
              >
                Start Building Your Dream Trip
              </h2>

              <div className="grid w-full grid-cols-1 gap-6 min-[768px]:grid-cols-2 min-[768px]:gap-8 lg:grid-cols-4">
                {DESKTOP_CARDS.map((card) => (
                  <button
                    key={card.key}
                    type="button"
                    onClick={() => handleCardClick(card.action)}
                    className="flex cursor-pointer flex-col items-center gap-6 rounded-2xl bg-white/30 p-6 transition-transform hover:scale-[1.02] min-[768px]:gap-8 min-[768px]:p-8"
                  >
                    <div className="relative h-[242px] w-[200px]">
                      <Image src={card.image} alt={card.title} fill className="object-contain" />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-center font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.02em] text-[#252528]">
                        {card.title}
                      </span>
                      <span className="text-center font-manrope text-[14px] font-normal leading-5 tracking-[0.02em] text-[#252528]">
                        {card.subtitle}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </ResponsiveOverlay>
  );
};

export default ImportInspoModal;
